import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getCustomers,
  updateCustomerStatus,
  getCustomerDetail,
} from "../../api/customerApi";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import CustomerDetailModal from "../../components/pages/CustomerManagement/CustomerDetailModal";
import "./style.css";

const MySwal = withReactContent(Swal);

const CustomerManagementPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({ username: "", streak: "" });
  const [detailModal, setDetailModal] = useState({ isOpen: false, data: null });

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        username: filters.username || undefined,
        streak: filters.streak || undefined,
      };
      const response = await getCustomers(params);
      setCustomers(response.data?.items || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data?.total || 0,
      }));
    } catch (err) {
      console.error("Fetch Customers error:", err);
      if (err.response?.status !== 403) {
        MySwal.fire("Lỗi!", "Không thể tải danh sách khách hàng.", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.username, filters.streak]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateStatus = async (customer) => {
    MySwal.fire({
      title: "Xác nhận",
      text: `Bạn có chắc muốn thay đổi trạng thái của "${customer.username}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Đồng ý",
      cancelButtonText: "Hủy",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await updateCustomerStatus(customer._id);
          setCustomers((prev) =>
            prev.map((c) =>
              c._id === customer._id ? { ...c, status: !c.status } : c
            )
          );
          MySwal.fire(
            "Thành công!",
            "Trạng thái khách hàng đã được cập nhật.",
            "success"
          );
        } catch (err) {
          console.error("Update status error:", err);
          if (err.response?.status !== 403) {
            MySwal.fire(
              "Lỗi!",
              "Bạn không có quyền thực hiện chức năng này.",
              "error"
            );
          }
        }
      }
    });
  };

  const handleViewDetails = async (customer) => {
    try {
      const response = await getCustomerDetail(customer._id);
      setDetailModal({ isOpen: true, data: response.data });
    } catch (err) {
      console.error("Get details error:", err);
      if (err.response?.status !== 403) {
        MySwal.fire("Lỗi!", "Không thể lấy chi tiết khách hàng.", "error");
      }
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <>
      <div className="users-header">
        <div className="header-content">
          <h3>Quản lý điểm danh</h3>
        </div>
      </div>

      <div className="table-controls">
        <input
          type="text"
          name="username"
          placeholder="Tìm theo tài khoản..."
          className="search-input"
          value={filters.username}
          onChange={handleFilterChange}
        />
        <select
          name="streak"
          className="filter-select"
          value={filters.streak}
          onChange={handleFilterChange}
        >
          <option value="">Tất cả</option>
          {[...Array(7).keys()].map((i) => (
            <option key={i + 1} value={i + 1}>
              Chuỗi {i + 1}
            </option>
          ))}
        </select>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Chuỗi điểm danh</th>
              <th>Điểm danh lần cuối</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="table-message">
                  Đang tải...
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan="5" className="table-message">
                  Không tìm thấy khách hàng nào.
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer._id}>
                  <td>
                    <strong>{customer.username}</strong>
                  </td>
                  <td>{customer.current_streak}</td>
                  <td>
                    {customer.last_checkin_date
                      ? new Date(
                          customer.last_checkin_date
                        ).toLocaleDateString()
                      : ""}
                  </td>
                  <td>
                    <button
                      className={`status-toggle-btn ${
                        customer.status ? "status-active" : "status-inactive"
                      }`}
                      onClick={() => handleUpdateStatus(customer)}
                    >
                      {customer.status ? "Hoạt động" : "Đang khóa"}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn"
                        title="Xem chi tiết"
                        onClick={() => handleViewDetails(customer)}
                      >
                        👁️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-summary">
          Trang <strong>{pagination.page}</strong> /{" "}
          <strong>{totalPages}</strong> (Tổng: {pagination.total})
        </div>
        <div className="pagination-controls">
          <button
            onClick={() =>
              setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))
            }
            disabled={pagination.page <= 1}
          >
            Trước
          </button>
          <button
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: Math.min(totalPages, p.page + 1),
              }))
            }
            disabled={pagination.page >= totalPages}
          >
            Sau
          </button>
        </div>
      </div>

      <CustomerDetailModal
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, data: null })}
        details={detailModal.data}
      />
    </>
  );
};

export default CustomerManagementPage;
